import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/rootReducer';
import { Select, Table, TableColumnsType } from 'antd';

import Button from '../../Button';
import useAxios from '../../../hooks/useAxios';
import { getRollbook, updateRollbook, uploadRollbookFile } from '../../../api/campusClasses3.api';
import { IGetRollbook } from '../../../models/campusClasses3.model';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Spinner';
import styled from 'styled-components';
import useNotification from '../../../hooks/useNotification';
import useAuth from '../../../hooks/useAuth';
import { sendAttendanceMessages } from '../../../api/tutor.api';
import ShowRefundModal from './modals/ShowRefundModal';
import moment from 'moment';
import _ from 'lodash';

const { Option } = Select;

interface IProps {
  setTab: (tab: string) => void;
}

const Block = styled.div`
  .upload-label {
    width: 55px;
    font-weight: normal !important;
    position: relative;
    top: 4px;
  }
`;

const ClassRollbook: FC<IProps> = ({ setTab }) => {
  const { t, i18n } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetRollbook>();
  const { data: updateData, isLoading: updateIsLoading, error: updateError,
    execute: updateExecute } = useAxios();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError,
      execute: submitExecute } = useAxios();
  
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // CL0001 = 출결 상태
  const CL0001 = commonCodes.find((item) => item.commonCode === 'CL0001')?.detailCode;

  const [timetableCode, setTimetableCode] = useState<string|null>(null);
  const [inputs, setInputs] = useState<any>([]);
  const [showRefundVisible, setShowRefundVisible] = useState(false);
  const [attendCode, setAttendCode] = useState<number>();
  const [data, setData] = useState<any>([]);

  useEffect(() => {
    setTimetableCode(sessionStorage.getItem('timetableCode'));
    sessionStorage.removeItem('timetableCode');
  }, []);

  useEffect(() => {
    if (chosenClass && timetableCode) {
      doGetRollbook();
    }
  }, [chosenClass, timetableCode]);

  useEffect(() => {
    if (res?.data && !isLoading) {
      const _data = _.sortBy(_.cloneDeep(res?.data), 'studentKorName');
      setInputs(_data);
      setData(_data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (updateData && !updateIsLoading) {
      successNotification(t('common.message.save1'));
      doGetRollbook();
    }
  }, [updateData, updateIsLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      successNotification(t('common.message.submit'));
      doGetRollbook();
    }
  }, [submitRes, submitIsLoading]);

  const doGetRollbook = () => {
    if (chosenClass && timetableCode) {
      execute(getRollbook(chosenClass.classCode, timetableCode));
    }
  }

  const onSetInputs = (index: number, key: string, value: any) => {
    const newInputs = [...inputs];
    newInputs[index][key] = value;
    setInputs(newInputs);
  };

  const onSave = (index: number) => {
    if (!chosenClass || !timetableCode) return;
    if (!inputs[index].attendStatus) {
      inputs[index].attendStatus = 'AT';
    }

    const req = [
      {
        attendCode: inputs[index].attendCode,
        classStudentCode: inputs[index].classStudentCode,
        studentCode: inputs[index].memberCode,
        attendStatus: inputs[index].attendStatus,
        memo: inputs[index].memo,
      },
    ];

    updateExecute(updateRollbook(chosenClass.classCode, timetableCode, req));
  };

  const onSaveAll = () => {
    if (!chosenClass || !timetableCode) return;
    const req = [];
    for (let i = 0; i < inputs.length; i++) {
      if (!inputs[i].attendStatus) {
        inputs[i].attendStatus = 'AT';
      }
      req.push({
        attendCode: inputs[i].attendCode,
        classStudentCode: inputs[i].classStudentCode,
        studentCode: inputs[i].memberCode,
        attendStatus: inputs[i].attendStatus,
        memo: inputs[i].memo,
      });
    }

    updateExecute(updateRollbook(chosenClass.classCode, timetableCode, req));
  };

  const uploadFile = (index: number, file: any) => {
    if (!chosenClass || !timetableCode) return;

    const formData = new FormData();
    formData.append('attendCode', inputs[index].attendCode);
    formData.append('attachFileCode', '');
    formData.append('attachFileStatus', 'create');
    formData.append('attachFile', file);

    updateExecute(
      uploadRollbookFile(chosenClass.classCode, timetableCode, formData),
    );
  };

  const onSendMessages = () => {
    const req: any = [];
    data.forEach((item: any) => {
      if (item.attendCode) {
        req.push({ attendCode: item.attendCode });
      }
    });

    submitExecute(sendAttendanceMessages(req));
  }

  const onSendMessage = (attendCode: number) => {
    submitExecute(sendAttendanceMessages([{ attendCode }]));
  }

  const columns: TableColumnsType<any> = [
    {
      title: 'No.',
      dataIndex: 'memberCode',
      key: 'memberCode',
      align: 'center',
      render: (value, record, index) => index + 1,
    },
    {
      title: t('class.class.column74'),
      dataIndex: 'studentKorName',
      key: 'studentKorName',
      align: 'center',
      render: (value, record) => `${value} (${record.studentEngName})`,
    },
    {
      title: t('class.class.column39'),
      dataIndex: 'attendStatus',
      key: 'attendStatus',
      align: 'center',
      render: (value, record, index) => (
        <Select
          className="w-100"
          placeholder={t('common.button.select')}
          value={value || 'AT'}
          onChange={(value: string) =>
            onSetInputs(index, 'attendStatus', value)
          }
        >
          {CL0001?.map((item: any) => {
            return (
              <Option key={item.detailCode} value={item.detailCode}>
                {i18n.language === 'ko' ? item.korName : item.engName}
              </Option>
            );
          })}
        </Select>
      ),
    },
    {
      title: t('class.class.column44'),
      dataIndex: 'memo',
      key: 'memo',
      align: 'center',
      render: (value, record, index) => (
        <input
          type="text"
          className="form-control input-text"
          defaultValue={value}
          onChange={(e) => onSetInputs(index, 'memo', e.target.value)}
        />
      ),
    },
    {
      title: t('common.button.save'),
      dataIndex: '',
      key: '',
      align: 'center',
      render: (value, record, index) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small pink"
            onClick={() => onSave(index)}
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ),
    },
    {
      title: t('class.class.column46'),
      dataIndex: 'refundYN',
      key: 'refundYN',
      align: 'center',
      render: (value: string, record) => {
        if (record.attendStatus === 'NT') {
          if (value === 'Y') return 'Y';
          return (
            <div className="comm-btn__area justify-content-center">
              <Button
                className="btn-basic mint"
                onClick={() => {
                  setAttendCode(record.attendCode);
                  setShowRefundVisible(true);
                }}
                text={t('class.class.column46')}
                disabled={record.attendStatus !== 'NT' || !record.lastUpdDttm}
                writeAuthYn={writeAuthYn}
              />
            </div>
          );
        }

        return 'N/A';
      }
    },
    {
      title: t('class.class.column45'),
      dataIndex: 'attachFileCode',
      key: 'attachFileCode',
      align: 'center',
      render: (value, record, index) => {
        if (value) {
          return (
            <a
              className="download-link"
              href={`${record.attachFile.fileLocation}`}
              download={record.attachFile.originalName}
            >
              {record.attachFile.originalName}
            </a>
          );
        } else {
          return record.attendStatus !== 'NT' || !record.lastUpdDttm ? (
            <div className="comm-btn__area justify-content-center">
              <Button
                className="btn-basic small mint"
                onClick={() => {}}
                text={t('class.class.column45')}
                disabled
                writeAuthYn={writeAuthYn}
              />
            </div>
          ) : (
            <>
              <label
                className="btn btn-sm btn-success upload-label"
                htmlFor={`input_file_${index}`}
              >
                {t('class.class.column45')}
              </label>
              <input
                type="file"
                id={`input_file_${index}`}
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) uploadFile(index, e.target.files[0]);
                }}
              />
            </>
          );
        }
      },
    },
    {
      title: t('class.class.column75'),
      dataIndex: 'firstRegDttm',
      key: 'firstRegDttm',
      align: 'center',
      render: (value) => {
        return value ? moment(value).format('YYYY.MM.DD HH:mm:ss') : null;
      },
    },
    {
      title: t('class.class.column76'),
      dataIndex: 'lastUpdDttm',
      key: 'lastUpdDttm',
      align: 'center',
      render: (value) => {
        return value ? moment(value).format('YYYY.MM.DD HH:mm:ss') : null;
      },
    },
    {
      title: t('class.class.column77'),
      dataIndex: 'smsSendDatetime',
      key: 'smsSendDatetime',
      align: 'center',
      render: (value) => {
        return value ? moment(value).format('YYYY.MM.DD HH:mm:ss') : null;
      },
    },
    {
      title: t('class.class.column78'),
      dataIndex: 'attendCode',
      key: 'attendCode',
      align: 'center',
      render: (value, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small pink-line"
            onClick={() => onSendMessage(value)}
            text={t('class.class.button10')}
            writeAuthYn={writeAuthYn}
            disabled={!value}
          />
        </div>
      ),
    },
  ];

  return (
    <Block>
      <ErrorResponse error={error} />
      <ErrorResponse error={updateError} />
      <ErrorResponse error={submitError} />
      <div className="comm-bx__between">
        <div><h3 className="sub-title">{chosenClass?.className}</h3></div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink-line"
            onClick={onSendMessages}
            text={t('class.class.button11')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      <div className='comm-table__area'>
        <Table
          columns={columns}
          dataSource={data}
          pagination={{
            pageSize: 99999,
          }}
          rowKey="classStudentCode"
        />
      </div>

      <div className="comm-bx__between"> 
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => setTab('schedule')}
            text={t('common.button.back')}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={onSaveAll}
            text={t('class.class.button12')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      {showRefundVisible &&
        <ShowRefundModal
          visible={showRefundVisible}
          setVisible={setShowRefundVisible}
          attendCode={attendCode}
          doGetRollbook={doGetRollbook}
        />
      }
    </Block>
  );
};

export default ClassRollbook;
